// Projektnivå: hur mycket av processen som är aktuell i just det här projektet.
// Nivån beräknas av servern utifrån objektiva uppgifter och kan inte väljas
// manuellt. Den höjs automatiskt när projektet växer och sänks aldrig av sig själv.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  History,
  Info,
  ListChecks,
  Lock,
  RefreshCw,
  SlidersHorizontal,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { PROCESS_STEPS } from "@/lib/basProcess/constants";
import {
  DETAIL_LEVEL_DESCRIPTIONS,
  DETAIL_LEVEL_LABELS,
  DOCS_BY_LEVEL,
  fetchDetailLevel,
  hiddenDocsForLevel,
  type DetailLevel,
  type DetailLevelState,
} from "@/lib/basProcess/detailLevel";
import {
  applyLevel,
  fetchLevelHistory,
  fetchOverrideRequests,
  fetchScaleFacts,
  fetchSplitCandidates,
  requestLevelOverride,
  type LevelHistoryEntry,
  type OverrideRequest,
  type SplitCandidate,
} from "@/lib/basProcess/projectLevel";
import MiniAmpWizard from "./MiniAmpWizard";
import ScaleFactsDialog from "./ScaleFactsDialog";

interface Props {
  projectId: string;
  /** Anropas när dokumentens status ändrats. */
  onChanged?: () => void;
}

const LEVELS: DetailLevel[] = ["enkel", "standard", "full"];

const docLabel = (doc: string) => {
  const def = PROCESS_STEPS.find((s) => s.docNumber === doc);
  return `Dok ${doc} ${def?.title ?? ""}`.trim();
};

const dateText = (value: string) =>
  new Date(value).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });

const DetailLevelPanel: React.FC<Props> = ({ projectId, onChanged }) => {
  const [state, setState] = useState<DetailLevelState | null>(null);
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [factsOpen, setFactsOpen] = useState(false);
  const [hasFacts, setHasFacts] = useState(true);
  const [history, setHistory] = useState<LevelHistoryEntry[]>([]);
  const [splits, setSplits] = useState<SplitCandidate[]>([]);
  const [requests, setRequests] = useState<OverrideRequest[]>([]);
  const [overrideLevel, setOverrideLevel] = useState<DetailLevel | null>(null);
  const [motivation, setMotivation] = useState("");

  const load = useCallback(async () => {
    const [next, hist, split, reqs, facts] = await Promise.all([
      fetchDetailLevel(projectId),
      fetchLevelHistory(projectId),
      fetchSplitCandidates(projectId),
      fetchOverrideRequests(projectId),
      fetchScaleFacts(projectId),
    ]);
    setState(next);
    setHistory(hist);
    setSplits(split);
    setRequests(reqs);
    setHasFacts(
      Boolean(
        facts.contract_value ||
          facts.gross_area ||
          facts.duration_months ||
          facts.contractor_count
      )
    );
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const hidden = useMemo(() => (state ? hiddenDocsForLevel(state.level) : []), [state]);
  const pendingRequest = requests.find((r) => r.status === "pending");

  if (!state) return null;

  const recalc = async () => {
    setBusy(true);
    try {
      const result = await applyLevel(projectId, "manual_recalc");
      toast.success(
        result.changed
          ? `Nivån har höjts till ${DETAIL_LEVEL_LABELS[result.level]}`
          : `Nivån är oförändrad (${DETAIL_LEVEL_LABELS[result.level]})`
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte räkna om nivån");
    } finally {
      setBusy(false);
    }
  };

  const sendOverride = async () => {
    if (!overrideLevel || motivation.trim().length < 20) {
      toast.error("Skriv en motivering på minst 20 tecken");
      return;
    }
    setBusy(true);
    try {
      await requestLevelOverride(projectId, overrideLevel, motivation.trim(), state.computed);
      toast.success("Begäran skickad för prövning");
      setOverrideLevel(null);
      setMotivation("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte skicka begäran");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Projektets nivå</span>
          <Badge variant="secondary">{DETAIL_LEVEL_LABELS[state.level]}</Badge>
          <Badge variant="outline" className="gap-1">
            <Lock className="h-3 w-3" /> Beräknad
          </Badge>
          {state.raisedAboveComputed && <Badge variant="outline">Höjd tidigare i projektet</Badge>}
        </div>

        <p className="text-sm text-muted-foreground">
          {DETAIL_LEVEL_DESCRIPTIONS[state.level]} {DOCS_BY_LEVEL[state.level].length} av{" "}
          {PROCESS_STEPS.length} dokument är aktuella. Nivån räknas fram ur projektets omfattning
          och går inte att välja fritt – kraven i lagen är desamma oavsett nivå.
        </p>

        {!hasFacts && (
          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              Uppgifterna om projektets omfattning saknas. Tills de fyllts i ligger projektet på
              Standard.
            </span>
          </div>
        )}

        {state.reasons.length > 0 && (
          <ul className="space-y-1 text-sm text-muted-foreground">
            {state.reasons.map((r) => (
              <li key={r} className="flex gap-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setFactsOpen(true)} disabled={busy}>
            Uppdatera projektuppgifter
          </Button>
          <Button size="sm" variant="outline" onClick={recalc} disabled={busy}>
            <RefreshCw className="mr-1 h-4 w-4" /> Räkna om
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowHistory((s) => !s)}
            disabled={history.length === 0}
          >
            <History className="mr-1 h-4 w-4" /> Nivåhistorik ({history.length})
          </Button>
        </div>

        {showHistory && history.length > 0 && (
          <ul className="space-y-2 rounded-md border bg-background p-3 text-sm">
            {history.map((h) => (
              <li key={h.id}>
                <span className="font-medium">
                  {h.from_level ? `${h.from_level} → ${h.to_level}` : h.to_level}
                </span>{" "}
                <span className="text-muted-foreground">
                  · {dateText(h.created_at)} · {h.source}
                </span>
                {h.reasons?.length > 0 && (
                  <p className="text-xs text-muted-foreground">{h.reasons.join(" · ")}</p>
                )}
              </li>
            ))}
          </ul>
        )}

        {splits.length > 0 && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <div className="flex gap-2 text-sm font-medium text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Projektet kan vara en del av ett större bygge
            </div>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {splits.map((s) => (
                <li key={s.sibling_id}>
                  {s.sibling_name} · {s.match_reason} · skapat {dateText(s.created_at)}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              När ett bygge delas upp bedöms omfattningen samlat. Kontrollera att uppgifterna avser
              hela entreprenaden.
            </p>
          </div>
        )}

        {pendingRequest ? (
          <div className="rounded-md border bg-background p-3 text-sm">
            <p className="font-medium">
              Begäran om nivå {DETAIL_LEVEL_LABELS[pendingRequest.requested_level as DetailLevel]}{" "}
              väntar på prövning
            </p>
            <p className="mt-1 text-muted-foreground">{pendingRequest.motivation}</p>
          </div>
        ) : (
          <div className="rounded-md border bg-background p-3">
            <p className="text-sm font-medium">Behöver nivån prövas?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Nivån kan inte ändras i projektet. Om beräkningen blivit fel kan du begära en
              prövning med skriftlig motivering – den loggas och beslutas av administratör.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {LEVELS.map((lvl) => (
                <Button
                  key={lvl}
                  size="sm"
                  variant={overrideLevel === lvl ? "default" : "outline"}
                  disabled={busy || lvl === state.level}
                  onClick={() => setOverrideLevel(lvl)}
                >
                  {DETAIL_LEVEL_LABELS[lvl]}
                </Button>
              ))}
            </div>
            {overrideLevel && (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  placeholder="Beskriv varför den beräknade nivån inte stämmer med projektets faktiska omfattning."
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={sendOverride} disabled={busy}>
                    Skicka begäran
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOverrideLevel(null)}>
                    Avbryt
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {state.level === "enkel" && (
          <div className="rounded-md border bg-background p-3">
            <p className="text-sm font-medium">Dok 60 · Arbetsmiljöplan liten omfattning</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Fyll i ett samlat formulär i fem korta steg. Svaren skrivs till arbetsmiljöplanen och
              arbetsområdesutredningen – samma underlag som i den fullständiga processen.
            </p>
            <Button className="mt-3" size="sm" onClick={() => setWizardOpen(true)}>
              <ListChecks className="mr-1 h-4 w-4" /> Öppna minidokumentet
            </Button>
          </div>
        )}

        {hidden.length > 0 && (
          <div>
            <Button variant="link" size="sm" className="px-0" onClick={() => setShowDocs((s) => !s)}>
              {showDocs ? "Dölj" : `Visa de ${hidden.length} dokument som inte är aktuella`}
            </Button>
            {showDocs && (
              <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                {hidden.map((d) => (
                  <li key={d}>{docLabel(d)}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <MiniAmpWizard
          projectId={projectId}
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          onSaved={onChanged}
        />
        <ScaleFactsDialog
          projectId={projectId}
          open={factsOpen}
          onOpenChange={setFactsOpen}
          onSaved={async () => {
            await load();
            onChanged?.();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default DetailLevelPanel;
